import { Calendar, Clock, Search, User, Package, ShoppingBag,
  Building2, TrendingUp, MapPin, Star, Dumbbell, ArrowRight,
  Loader2, MapPin as MapPinIcon, CalendarPlus } from 'lucide-react';
import {
  useClubs, useCoaches, useCourtBookings, useCoachBookings,
  useReviews, useMachines,
} from '../../hooks/useAPI';
import { useShopCollections, useShopProducts } from '../../hooks/useShopify';
import { formatShopifyMoney, getShopProductCategory, shopifyConfigError, type ShopProduct } from '../../lib/shopify';
import { useShopStore } from '../../store/shopStore';
import { useAuthStore } from '../../store/authStore';

interface HomeScreenProps {
  onNavigate: (screen: any, params?: any) => void;
}

export default function HomeScreen({ onNavigate }: HomeScreenProps) {
  const { user, profile } = useAuthStore();
  const { data: clubs } = useClubs();
  const { data: coaches } = useCoaches();
  const { data: courtBookings } = useCourtBookings();
  const { data: coachBookings } = useCoachBookings();
  const { data: reviews } = useReviews();
  const { data: machines } = useMachines();
  const {
    products: shopProducts,
    error: shopError,
    isLoading: isShopLoading,
    isConfigured: isShopConfigured,
  } = useShopProducts();
  const { collections: shopCollections } = useShopCollections();
  const recentlyViewed = useShopStore((state) => state.recentlyViewed);

  const clubList   = ((clubs   as any[]) ?? []).slice(0, 6);
  const coachList  = ((coaches as any[]) ?? []).slice(0, 6);
  const reviewList = ((reviews as any[]) ?? []).slice(0, 2);
  const featuredShopProducts = shopProducts.slice(0, 4);

  const now = new Date();
  const upcoming = [
    ...((courtBookings as any[]) ?? []).map((b: any) => ({
      kind: 'court' as const,
      id: b.id,
      when: new Date(b.start_at),
      title: b.courts?.name ?? 'Court booking',
      subtitle: b.clubs?.name ?? '',
      ref: b.booking_reference,
    })),
    ...((coachBookings as any[]) ?? []).map((b: any) => ({
      kind: 'coach' as const,
      id: b.id,
      when: new Date(b.scheduled_at),
      title: b.coaches?.display_name ? `Lesson with ${b.coaches.display_name}` : 'Lesson',
      subtitle: `${b.lesson_type?.replace('_', ' ') ?? ''} · ${b.environment ?? ''}`,
      ref: b.booking_reference,
    })),
  ]
    .filter((b) => b.when >= now)
    .sort((a, b) => a.when.getTime() - b.when.getTime())
    .slice(0, 1)[0];

  const hasMachine = !!machines && (machines as any[]).length > 0;

  // Personal touches layered on the brand hero: time-of-day greeting
  // + initials-on-navy avatar opposite a Lime Ace eyebrow. The
  // headline and 3-card primary actions stay as the brand anchor
  // they were before.
  //
  // Name resolution chain — try the richest source first:
  //   1. profiles.full_name (server-side row, may be empty for users
  //      who haven't completed onboarding)
  //   2. auth.user.user_metadata.full_name (set by the Sign-up form)
  //   3. email prefix
  //   4. literal 'Player'
  const greeting = greetingForHour(now.getHours());
  const metaName = (user?.user_metadata as { full_name?: string } | undefined)?.full_name;
  const displayName = (
    profile?.full_name?.trim()
    || metaName?.trim()
    || user?.email?.split('@')[0]
    || 'Player'
  );
  const firstName = displayName.split(/\s+/)[0];
  const initials = initialsFor(displayName);

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      {/* Hero — Deep Navy band, brand eyebrow + display headline. */}
      <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] text-white p-6 pb-10 rounded-b-[2rem]">
        <div className="flex items-center justify-between mb-6">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#D4F04A] font-semibold">
              {greeting}
            </div>
            <div className="text-base mt-0.5 text-white/90 truncate">Hi, {firstName}</div>
          </div>
          <button
            onClick={() => onNavigate('profile')}
            aria-label="Profile"
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-[#D4F04A] flex items-center justify-center font-bold text-sm shrink-0"
          >
            {initials}
          </button>
        </div>

        <h1 className="text-3xl leading-tight mb-1 font-bold tracking-tight">
          Tennis. In one place.
        </h1>
        <p className="text-sm opacity-80">
          Book a court, find a coach, or explore Dubai's clubs.
        </p>
      </div>

      {/* Next match — only when an upcoming booking exists. Lime Ace
          eyebrow + lime CTA + outline secondary, slides up over the
          hero's bottom radius. */}
      {upcoming && (
        <div className="px-5 -mt-6 mb-4">
          <div className="rounded-2xl bg-gradient-to-b from-[#0F1E36] to-[#16294A] text-white p-5 relative overflow-hidden shadow-md">
            <div className="absolute bottom-0 left-5 right-5 h-px bg-[#D4F04A]" />
            <span className="text-[11px] uppercase tracking-[0.16em] text-[#D4F04A] font-semibold">
              Next {upcoming.kind === 'court' ? 'match' : 'lesson'}
            </span>
            <div className="font-bold text-[22px] leading-tight mt-2 truncate">
              {upcoming.title}
              {upcoming.subtitle && <span className="opacity-90 font-normal"> · {upcoming.subtitle}</span>}
            </div>
            <div className="flex gap-4 mt-3 text-[13px] text-white/85 flex-wrap">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[#D4F04A]" />
                {formatBookingWhen(upcoming.when, now)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#D4F04A]" />
                {upcoming.when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => onNavigate('orders')}
                className="flex-1 h-10 px-4 rounded-xl bg-[#D4F04A] text-[#0F1E36] text-sm font-semibold inline-flex items-center justify-center gap-1.5"
              >
                <MapPinIcon className="w-4 h-4" />
                Get directions
              </button>
              <button
                onClick={() => onNavigate('orders')}
                className="h-10 px-4 rounded-xl bg-transparent text-white border-[1.5px] border-white/40 text-sm font-semibold inline-flex items-center justify-center gap-1.5"
              >
                <CalendarPlus className="w-4 h-4" />
                Manage
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Primary actions — three big cards, "Book a court" gets the
          filled green treatment to draw the eye to the fastest path.
          Slides up over the hero radius when there's no upcoming. */}
      <div className={`px-6 ${upcoming ? '' : '-mt-6'} mb-8`}>
        <div className="grid grid-cols-3 gap-3">
          <PrimaryAction
            icon={Clock} label="Book a court" subtitle="Reserve now" accent
            onClick={() => onNavigate('courts')}
          />
          <PrimaryAction
            icon={User} label="Find a coach" subtitle="Lessons & packages"
            onClick={() => onNavigate('coaches')}
          />
          <PrimaryAction
            icon={Building2} label="Browse clubs" subtitle="See venues near you"
            onClick={() => onNavigate('clubs')}
          />
        </div>
      </div>

      <Section title="Shop gear" action="Open shop" onAction={() => onNavigate('shop')}>
        <div className="px-6 space-y-3">
          {isShopConfigured && shopCollections.length > 0 && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {shopCollections.map((collection) => (
                <button
                  key={collection.id}
                  onClick={() => onNavigate('shop-collection', { collectionHandle: collection.handle })}
                  className="h-8 whitespace-nowrap rounded-full bg-secondary px-3 text-xs text-muted-foreground flex items-center hover:text-foreground"
                >
                  {collection.title}
                </button>
              ))}
            </div>
          )}

          {isShopConfigured && isShopLoading && (
            <div className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading live shop products…
            </div>
          )}

          {isShopConfigured && !isShopLoading && recentlyViewed.length > 0 && (
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {recentlyViewed.map((item) => (
                <HomeRecentShopCard
                  key={item.productHandle}
                  item={item}
                  onOpen={() => onNavigate('shop-product', { handle: item.productHandle })}
                />
              ))}
            </div>
          )}

          {isShopConfigured && !isShopLoading && recentlyViewed.length === 0 && featuredShopProducts.length > 0 && (
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {featuredShopProducts.map((product) => (
                <ShopPreviewCard
                  key={product.id}
                  product={product}
                  onOpen={() => onNavigate('shop-product', { handle: product.handle })}
                />
              ))}
            </div>
          )}

          {(!isShopConfigured || (!isShopLoading && featuredShopProducts.length === 0) || shopError) && (
            <button
              onClick={() => onNavigate('shop')}
              className="w-full text-left bg-card border border-border rounded-2xl p-4 flex items-center gap-3 hover:border-[#1A6B4F]/40 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-[#D4F04A]/20 text-[#1A6B4F] flex items-center justify-center shrink-0">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">Shop tennis gear</div>
                <div className="text-xs text-muted-foreground">
                  {shopError?.message ?? (isShopConfigured ? 'Open the live catalog in the shop.' : (shopifyConfigError ?? 'Add Shopify storefront env vars to enable the shop.'))}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </Section>

      {/* Featured clubs */}
      {clubList.length > 0 && (
        <Section title="Featured clubs" action="See all" onAction={() => onNavigate('clubs')}>
          <div className="flex gap-3 overflow-x-auto no-scrollbar px-6 pb-1">
            {clubList.map((c: any) => (
              <button
                key={c.id}
                onClick={() => onNavigate('club', { id: c.id })}
                className="flex-shrink-0 w-56 text-left bg-card border border-border rounded-2xl overflow-hidden hover:border-[#1A6B4F]/40 transition-colors"
              >
                <div className="h-28 bg-secondary relative">
                  {c.photos?.[0] && (
                    <img src={typeof c.photos[0] === 'string' ? c.photos[0] : c.photos[0]?.url}
                         alt="" className="w-full h-full object-cover" />
                  )}
                  {c.logo_url && (
                    <img src={c.logo_url} alt=""
                      className="absolute bottom-2 left-2 w-8 h-8 rounded-md object-cover border border-white shadow-sm" />
                  )}
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  {c.address && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                      <MapPin className="w-3 h-3 shrink-0" />
                      <span className="truncate">{c.address}</span>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Featured coaches */}
      {coachList.length > 0 && (
        <Section title="Coaches you can book" action="See all" onAction={() => onNavigate('coaches')}>
          <div className="flex gap-3 overflow-x-auto no-scrollbar px-6 pb-1">
            {coachList.map((c: any) => (
              <button
                key={c.id}
                onClick={() => onNavigate('coach', { id: c.id })}
                className="flex-shrink-0 w-48 text-left bg-card border border-border rounded-2xl p-3 hover:border-[#1A6B4F]/40 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-secondary overflow-hidden flex items-center justify-center mb-2">
                  {c.photo_url
                    ? <img src={c.photo_url} alt="" className="w-full h-full object-cover" />
                    : <User className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="text-sm font-medium truncate">{c.display_name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                  {c.years_experience && (
                    <span className="flex items-center gap-1"><Star className="w-3 h-3" />{c.years_experience}y</span>
                  )}
                  <span className="capitalize text-[10px] px-1.5 py-0.5 rounded-full bg-secondary">
                    {c.type}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* What people say */}
      {reviewList.length > 0 && (
        <Section title="What players say">
          <div className="px-6 grid gap-3">
            {reviewList.map((r: any) => (
              <div key={r.id} className="bg-card border border-border rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-3.5 h-3.5 ${
                        n <= (r.rating ?? 0) ? 'fill-[#D4F04A] text-[#D4F04A]' : 'text-border'
                      }`} />
                    ))}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.profiles?.full_name ?? 'Deuce player'}
                  </div>
                </div>
                {r.comment && <p className="text-sm">{r.comment}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Legacy: rental machines — opt-in, only if any live */}
      {hasMachine && (
        <Section title="Ball machines" action="Rent one" onAction={() => onNavigate('product')}>
          <div className="px-6">
            <button
              onClick={() => onNavigate('product')}
              className="w-full text-left bg-gradient-to-r from-[#D4F04A]/30 to-transparent rounded-2xl p-4 border border-[#1A6B4F]/20 flex items-center gap-3"
            >
              <div className="w-11 h-11 rounded-xl bg-[#1A6B4F] text-white flex items-center justify-center">
                <Dumbbell className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">Tennis ball machine rental</div>
                <div className="text-xs text-muted-foreground">
                  Practice alone — delivered to your court.
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#1A6B4F]" />
            </button>
          </div>
        </Section>
      )}

      {/* Footer bookings shortcut */}
      <div className="px-6 mt-4">
        <button
          onClick={() => onNavigate('orders')}
          className="w-full h-12 rounded-xl bg-secondary hover:bg-muted text-sm flex items-center justify-center gap-2"
        >
          <TrendingUp className="w-4 h-4" /> My bookings
        </button>
      </div>

      <SentrySmokeTest />
    </div>
  );
}

/**
 * Hidden diagnostic button. Only shows when the URL carries
 * ?sentry-test=1 so it's invisible to real customers. Throws an
 * uncaught Error on click, which Sentry catches via the global
 * handler + ErrorBoundary.
 */
function SentrySmokeTest() {
  if (typeof window === 'undefined') return null;
  const show = new URLSearchParams(window.location.search).get('sentry-test') === '1';
  if (!show) return null;
  return (
    <div className="px-6 mt-6">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="text-sm font-medium text-amber-900 mb-1">Sentry smoke test</div>
        <p className="text-xs text-amber-900/80 mb-3">
          Click to throw a test error. Visible only with <code>?sentry-test=1</code> in the URL.
        </p>
        <button
          onClick={() => { throw new Error('Sentry smoke test at ' + new Date().toISOString()); }}
          className="h-10 px-4 rounded-lg bg-red-600 text-white text-sm"
        >
          Break the world
        </button>
      </div>
    </div>
  );
}

function PrimaryAction({
  icon: Icon, label, subtitle, accent, onClick,
}: {
  icon: any;
  label: string;
  subtitle: string;
  accent?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`group h-32 rounded-2xl border transition-all p-3.5 flex flex-col items-start justify-between text-left ${
        accent
          ? 'bg-[#1A6B4F] border-[#1A6B4F] text-white hover:bg-[#14563F] shadow-md'
          : 'bg-card border-border hover:border-[#1A6B4F]/60'
      }`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
        accent
          ? 'bg-[#D4F04A] text-[#14563F]'
          : 'bg-[#D4F04A]/20 text-[#1A6B4F] group-hover:bg-[#D4F04A]/40'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 w-full">
        <div className="text-sm font-medium leading-tight">{label}</div>
        <div className={`text-[11px] mt-0.5 leading-tight truncate ${
          accent ? 'opacity-85' : 'text-muted-foreground'
        }`}>
          {subtitle}
        </div>
      </div>
    </button>
  );
}

function greetingForHour(hour: number): string {
  const dayPart = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  // Approximate the bundle's "Friday afternoon" header. Falls back to
  // a plain "Good {part}" when the day name is unknown.
  return `${new Date().toLocaleDateString('en-GB', { weekday: 'long' })} ${dayPart}`;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatBookingWhen(when: Date, now: Date): string {
  const sameDay =
    when.getFullYear() === now.getFullYear()
    && when.getMonth() === now.getMonth()
    && when.getDate() === now.getDate();
  if (sameDay) return 'Today';
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const sameTomorrow =
    when.getFullYear() === tomorrow.getFullYear()
    && when.getMonth() === tomorrow.getMonth()
    && when.getDate() === tomorrow.getDate();
  if (sameTomorrow) return 'Tomorrow';
  return when.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}

function ShopPreviewCard({
  product,
  onOpen,
}: {
  product: ShopProduct;
  onOpen: () => void;
}) {
  const image = product.featuredImage ?? product.images[0] ?? null;

  return (
    <button
      onClick={onOpen}
      className="w-64 shrink-0 overflow-hidden rounded-[1.6rem] border border-border bg-card text-left transition-colors hover:border-[#1A6B4F]/40"
    >
      <div className="h-36 bg-secondary">
        {image ? (
          <img src={image.url} alt={image.altText ?? product.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-sm font-medium line-clamp-2">{product.title}</div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              {getShopProductCategory(product)}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
        </div>
        <div className="mt-3 text-sm font-medium text-[#1A6B4F]">
          From {formatShopifyMoney(product.priceRange.minVariantPrice)}
        </div>
      </div>
    </button>
  );
}

function HomeRecentShopCard({
  item,
  onOpen,
}: {
  item: ReturnType<typeof useShopStore.getState>['recentlyViewed'][number];
  onOpen: () => void;
}) {
  return (
    <button
      onClick={onOpen}
      className="w-56 shrink-0 overflow-hidden rounded-[1.6rem] border border-border bg-card text-left transition-colors hover:border-[#1A6B4F]/40"
    >
      <div className="h-32 bg-secondary">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.imageAlt ?? item.productTitle} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No image</div>
        )}
      </div>
      <div className="p-4">
        <div className="text-sm font-medium line-clamp-2">{item.productTitle}</div>
        <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{item.category}</div>
        <div className="mt-3 text-sm font-medium text-[#1A6B4F]">
          {formatShopifyMoney({ amount: String(item.priceAmount), currencyCode: item.currencyCode })}
        </div>
      </div>
    </button>
  );
}

function Section({
  title, action, onAction, children,
}: { title: string; action?: string; onAction?: () => void; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <div className="px-6 mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">{title}</h2>
        {action && onAction && (
          <button onClick={onAction} className="text-xs text-[#1A6B4F] hover:underline flex items-center gap-1">
            {action} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

/** Added for TS: Calendar import isn't used in the primary tiles but we
 * keep it available if we later want a "schedule" pill. */
const _Calendar = Calendar;
